import {
  DEFAULT_WAITWHILE_SERVICE_ID,
  NO_PREFERENCE_IMAGE,
} from '../../../config/constants'
import { apiFetch } from '../../../lib/api/client'
import { getEndDate, getStartDate } from '../../../lib/date-helper'
import { useBookingStore } from '../../../store'
import type { Employee } from '../../EmployeesList/EmployeesList.types'

/**
 * Fetches a single employee by their CRM employeeId using the v3 endpoint.
 * Always uses locationCode — no storeId fallback.
 */
export async function getEmployeeDataById(
  employeeId: string,
): Promise<Employee> {
  const { env, locationCode, serviceId, wwLocationId } =
    useBookingStore.getState()

  const waitwhileServiceId = serviceId ?? DEFAULT_WAITWHILE_SERVICE_ID[env]

  const params = new URLSearchParams({
    locationCode: locationCode ?? '',
    waitwhileServiceId,
    startDate: getStartDate(),
    endDate: getEndDate(),
    waitwhileLocationId: wwLocationId ?? '',
    employeeId,
  })

  return apiFetch<Employee>(`v3/employee/employeeId?${params.toString()}`)
}

/**
 * Fetches an employee by their Waitwhile resource id (used in reschedule when
 * the booking response only carries resourceIds, not the CRM employeeId).
 */
export async function getEmployeeDataByWwResourceId(
  waitwhileResourceId: string,
): Promise<Employee> {
  const { env, locationCode, serviceId, wwLocationId } =
    useBookingStore.getState()

  const waitwhileServiceId = serviceId ?? DEFAULT_WAITWHILE_SERVICE_ID[env]

  const params = new URLSearchParams({
    locationCode: locationCode ?? '',
    waitwhileServiceId,
    startDate: getStartDate(),
    endDate: getEndDate(),
    waitwhileLocationId: wwLocationId ?? '',
    waitwhileResourceId,
  })

  return apiFetch<Employee>(
    `v3/employee/waitwhileResourceId/?${params.toString()}`,
  )
}

/**
 * Fetches the no-preference employee (with availability) from the v4 employees endpoint.
 * Used on the timeslots screen when no specific employee was selected.
 */
export async function getNoPreferenceEmployee(): Promise<Employee | null> {
  const { env, locationCode, serviceId, wwLocationId } =
    useBookingStore.getState()

  const waitwhileServiceId = serviceId ?? DEFAULT_WAITWHILE_SERVICE_ID[env]

  const params = new URLSearchParams({
    locationCode: locationCode ?? '',
    waitwhileServiceId,
    startDate: getStartDate(),
    endDate: getEndDate(),
    waitwhileLocationId: wwLocationId ?? '',
  })

  const employees = await apiFetch<Employee[]>(
    `v4/employees?${params.toString()}`,
  )
  return employees.find((e) => e.isNoPreferenceEmployee) ?? null
}

/** Resolves the employee image URL, falling back to the no-preference image. */
export function resolveEmployeeImage(employee: Employee): string {
  return (
    employee.imageUrl ?? employee.imageAlternativeUrl ?? NO_PREFERENCE_IMAGE
  )
}
